import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { FieldMessage, Switch, TextField, fieldDescription } from '../ui';
import { useConsoleI18n, locale } from './bind';

// Form fields beyond the primitives: a label for any control, numbers, dates, a debounced search, segmented choices,
// colours and a switch with a hint. All use the `.nyk-ui-field` anatomy (label, control, hint or error).

/** What `Field` hands a control given as a function: its id and description. */
export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
}

/**
 * A visible label (with hint or error) around any control (a `Select`, `AsyncSelect`, a custom picker). The hint or
 * error describes the control (`aria-describedby`, `aria-invalid` on an error):
 * - children as a function get the props to spread on the control (`{(p) => <Select {...p} … />}`);
 * - with `htmlFor` the label targets that control and the description is set on it;
 * - otherwise the wrapper is a group named by the label and described by the hint.
 */
export function Field({
  label,
  hint,
  error,
  htmlFor,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  className?: string;
  children: ReactNode | ((control: FieldControlProps) => ReactNode);
}) {
  const auto = useId();
  const labelId = `${auto}-label`;
  const render = typeof children === 'function' ? children : undefined;
  const controlId = htmlFor ?? (render ? auto : undefined);
  const ids = fieldDescription(auto, hint, error);
  const ref = useRef<HTMLDivElement>(null);
  // A control given as an element: put the description on it once it is in the document.
  useLayoutEffect(() => {
    if (render || !htmlFor || !ids.describedBy) return;
    const el = ref.current?.ownerDocument.getElementById(htmlFor);
    if (!el || !ref.current?.contains(el)) return;
    const before = el.getAttribute('aria-describedby');
    const hadInvalid = el.hasAttribute('aria-invalid');
    el.setAttribute('aria-describedby', [before, ids.describedBy].filter(Boolean).join(' '));
    if (error && !hadInvalid) el.setAttribute('aria-invalid', 'true');
    return () => {
      if (before) el.setAttribute('aria-describedby', before);
      else el.removeAttribute('aria-describedby');
      if (error && !hadInvalid) el.removeAttribute('aria-invalid');
    };
  }, [render, htmlFor, ids.describedBy, error]);
  return (
    <div
      ref={ref}
      className={['nyk-ui-field', className].filter(Boolean).join(' ')}
      role={controlId ? undefined : 'group'}
      aria-labelledby={controlId ? undefined : labelId}
      aria-describedby={controlId ? undefined : ids.describedBy}
    >
      {controlId ? (
        <label htmlFor={controlId} id={labelId}>
          {label}
        </label>
      ) : (
        <span id={labelId}>{label}</span>
      )}
      {render
        ? render({
            id: controlId!,
            'aria-describedby': ids.describedBy,
            'aria-invalid': error ? true : undefined,
          })
        : (children as ReactNode)}
      <FieldMessage hint={hint} error={error} ids={ids} />
    </div>
  );
}

/** "1 234,5" / "1,234.5" / "1234.5" → 1234.5; '' → null; anything else → NaN. */
export function parseNumber(text: string): number | null {
  const s = text.trim().replace(/[\s\u00a0\u202f]/g, '');
  if (!s) return null;
  // The last of `,` / `.` is the decimal separator when both occur; a lone comma is decimal (sv, fi, no, da, lt, pl).
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  const normalized = lastComma > lastDot ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  return /^[-+]?(\d+\.?\d*|\.\d+)$/.test(normalized) ? Number(normalized) : NaN;
}

const showNumber = (v: number | null) =>
  v == null ? '' : v.toLocaleString(locale(), { useGrouping: false, maximumFractionDigits: 10 });

/**
 * A number typed as text (decimal comma or point, no spinner), checked against `min`, `max` and `integer` while
 * typing. `onChange` gets the number, or null while the field is empty or invalid.
 */
export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  integer,
  required,
  hint,
  error,
  disabled,
  placeholder,
  unit,
}: {
  label: ReactNode;
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  integer?: boolean;
  required?: boolean;
  hint?: ReactNode;
  /** An error of the form (shown instead of the field's own). */
  error?: ReactNode;
  disabled?: boolean;
  placeholder?: string;
  /** Shown after the label: `kg`, `%`. */
  unit?: string;
}) {
  const { t } = useConsoleI18n();
  const [text, setText] = useState(() => showNumber(value ?? null));
  const [touched, setTouched] = useState(false);
  // Follow a value replaced from outside, never while it already matches what is typed.
  useEffect(() => {
    const typed = parseNumber(text);
    if ((value ?? null) !== (typed === null || Number.isNaN(typed) ? null : typed))
      setText(showNumber(value ?? null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const parsed = parseNumber(text);
  const own =
    parsed === null
      ? required && touched
        ? t('field.required')
        : null
      : Number.isNaN(parsed)
        ? t('field.notNumber')
        : integer && !Number.isInteger(parsed)
          ? t('field.wholeNumber')
          : min != null && parsed < min
            ? t('field.min', { min: showNumber(min) })
            : max != null && parsed > max
              ? t('field.max', { max: showNumber(max) })
              : null;
  return (
    <TextField
      label={unit ? `${label} (${unit})` : label}
      value={text}
      inputMode={integer ? 'numeric' : 'decimal'}
      disabled={disabled}
      placeholder={placeholder}
      required={required}
      aria-invalid={!!(error || own) || undefined}
      error={error ?? own ?? undefined}
      hint={hint}
      onChange={(e) => {
        setText(e.target.value);
        setTouched(true);
        const v = parseNumber(e.target.value);
        const ok =
          v !== null &&
          !Number.isNaN(v) &&
          !(integer && !Number.isInteger(v)) &&
          !(min != null && v < min) &&
          !(max != null && v > max);
        onChange(ok ? v : null);
      }}
    />
  );
}

/** A calendar date (`YYYY-MM-DD`, '' = none) with the browser's date picker. */
export function DateField({
  label,
  value,
  onChange,
  min,
  max,
  hint,
  error,
  disabled,
  required,
}: {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  hint?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  required?: boolean;
}) {
  return (
    <TextField
      type="date"
      label={label}
      value={value}
      min={min}
      max={max}
      hint={hint}
      error={error}
      disabled={disabled}
      required={required}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export interface DateRangeValue {
  from: string;
  to: string;
}

/** From / to dates side by side; `to` cannot be before `from`. Presets fill both (last 7 days, this month…). */
export function DateRange({
  label,
  value,
  onChange,
  presets,
  min,
  max,
}: {
  label?: ReactNode;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  presets?: { label: string; range: () => DateRangeValue }[];
  min?: string;
  max?: string;
}) {
  const { t } = useConsoleI18n();
  const reversed = !!value.from && !!value.to && value.to < value.from;
  const body = (
    <div className="nyk-date-range">
      <DateField
        label={t('field.from')}
        value={value.from}
        min={min}
        max={value.to || max}
        onChange={(from) => onChange({ ...value, from })}
      />
      <DateField
        label={t('field.to')}
        value={value.to}
        min={value.from || min}
        max={max}
        error={reversed ? t('field.toBeforeFrom') : undefined}
        onChange={(to) => onChange({ ...value, to })}
      />
      {presets && presets.length > 0 && (
        <div className="nyk-date-presets">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              className="nyk-ui-chip nyk-ui-chip-sm"
              onClick={() => onChange(p.range())}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
  return label ? <Field label={label}>{body}</Field> : body;
}

/** `YYYY-MM-DD` of a local date, `days` from today (negative = past): presets for `DateRange`. */
export function isoDay(days = 0, from: Date = new Date()): string {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * A search box for a server-side filter: typed text settles into `onChange` after `ms` (trimmed, '' → undefined) and
 * follows a value changed from outside (a cleared filter). It never filters loaded rows itself.
 */
export function DebouncedSearch({
  value,
  onChange,
  placeholder,
  label,
  ms = 350,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder: string;
  /** Accessible name (default: the placeholder). */
  label?: string;
  ms?: number;
}) {
  const [text, setText] = useState(value ?? '');
  useEffect(() => setText(value ?? ''), [value]);
  useEffect(() => {
    const settled = text.trim();
    if (settled === (value ?? '')) return;
    const timer = setTimeout(() => onChange(settled || undefined), ms);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return (
    <input
      className="nyk-ui-input nyk-search-input"
      type="search"
      aria-label={label ?? placeholder}
      placeholder={placeholder}
      value={text}
      onChange={(e) => setText(e.target.value)}
    />
  );
}

/**
 * One of a few options as a row of buttons (a radio group: arrow keys move the choice). Use `Tabs` for switching
 * views, this for a value of a form or a toolbar mode.
 */
export function SegmentedControl<T extends string>({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  /** Accessible name of the group (shown when `visibleLabel`). */
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: ReactNode; title?: string }[];
  disabled?: boolean;
}) {
  const move = (dir: number) => {
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + dir + options.length) % options.length];
    if (next) onChange(next.value);
  };
  return (
    <div
      className="nyk-ui-segment nyk-segmented"
      role="radiogroup"
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          move(1);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          move(-1);
        }
      }}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          tabIndex={value === o.value ? 0 : -1}
          title={o.title}
          disabled={disabled}
          className={`nyk-ui-segment-btn${value === o.value ? ' nyk-ui-selected' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const HEX = /^#[0-9a-f]{6}$/i;

/** A colour as `#rrggbb`: the browser's picker plus the hex text, kept in step. */
export function ColorField({
  label,
  value,
  onChange,
  hint,
  disabled,
}: {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  const { t } = useConsoleI18n();
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const id = useId();
  const invalid = !!text && !HEX.test(text);
  return (
    <Field label={label} htmlFor={id} hint={hint} error={invalid ? t('field.colour') : undefined}>
      <div className="nyk-color-field">
        <input
          type="color"
          aria-label={typeof label === 'string' ? label : undefined}
          value={HEX.test(value) ? value : '#000000'}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          id={id}
          className="nyk-ui-input"
          value={text}
          disabled={disabled}
          maxLength={7}
          spellCheck={false}
          onChange={(e) => {
            setText(e.target.value);
            if (HEX.test(e.target.value)) onChange(e.target.value.toLowerCase());
          }}
        />
      </div>
    </Field>
  );
}

/** A switch with a hint under it, laid out as a form field. */
export function SwitchField({
  label,
  checked,
  onChange,
  hint,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  const ids = fieldDescription(useId(), hint, undefined);
  return (
    <div className="nyk-ui-field nyk-switch-field">
      <Switch
        checked={checked}
        onChange={onChange}
        label={label}
        disabled={disabled}
        aria-describedby={ids.describedBy}
      />
      <FieldMessage hint={hint} ids={ids} />
    </div>
  );
}
